"use client";

import type { FC, ReactNode } from "react";
import { startTransition, useOptimistic } from "react";
import { cn } from "../../utils";
import GameFilterGroup from "./GameFilterGroup";
import type { GameFilterOption, GameFilterGroup as Group } from "./types";

type Selection = { groupId: string; value: string | undefined };

type Props = {
  title: string;
  showTitle?: boolean;
  groups: Group[];
  openGroupIds?: string[];
  footer?: ReactNode;
  className?: string;
  selectAction: (groupId: string, value: string | undefined) => void;
};

const applySelection = (groups: Group[], { groupId, value }: Selection) =>
  groups.map((group) =>
    group.id === groupId ? { ...group, selected: value } : group,
  );

const GameFilterPanel: FC<Props> = ({
  title,
  showTitle = true,
  groups,
  openGroupIds,
  footer,
  className,
  selectAction,
}) => {
  const [optimisticGroups, select] = useOptimistic(groups, applySelection);
  const [pending, setPending] = useOptimistic(false);

  const handleSelect = (groupId: string, value: string | undefined) =>
    startTransition(async () => {
      select({ groupId, value });
      setPending(true);
      await selectAction(groupId, value);
    });

  return (
    <aside
      aria-busy={pending}
      aria-label={title}
      data-pending={pending ? "" : undefined}
      data-slot="game-filter-panel"
      className={cn("flex flex-col gap-2", className)}
    >
      {showTitle && (
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
      )}
      {optimisticGroups.map((group) => (
        <GameFilterGroup
          key={group.id}
          group={group}
          defaultOpen={openGroupIds ? openGroupIds.includes(group.id) : true}
          onSelect={(value) => handleSelect(group.id, value)}
        />
      ))}
      {footer}
    </aside>
  );
};

export type { GameFilterOption, Group as GameFilterGroup };
export default GameFilterPanel;
